import * as stylex from "@stylexjs/stylex";
import { pipe } from "fp-ts/lib/pipeable";
import { AnimatePresence, motion } from "framer-motion";
import { useStateWithAutoRevert } from "hooks/useStateWithAutoRevert";
import { Check16 } from "icons/Check";
import { Copy16 } from "icons/Copy";
import { Download32 } from "icons/Download";
import { DownloadData16 } from "icons/DownloadData";
import { DownloadImage16 } from "icons/DownloadImage";
import { Info32 } from "icons/Info";
import { Share32 } from "icons/Share";
import { SocialMediaFacebook16 } from "icons/SocialMediaFacebook";
import { SocialMediaLinkedin16 } from "icons/SocialMediaLinkedin";
import { SocialMediaTwitter16 } from "icons/SocialMediaTwitter";
import React from "react";
import { defineMessage, FormattedMessage, useIntl } from "react-intl";
import { useStatefulFigureEmbedUrl, useStatefulFigureScreenshotUrl } from "../figures";
import * as M from "../materials";
import { colors } from "../materials/consts.stylex";
import { typography } from "../materials/typography.stylex";
import { O } from "../prelude";
import { InteractiveTooltip } from "./popover";
// It takes about this many milliseconds to capture a screenshot
const DOWNLOAD_TIMEOUT = 10000;
export const InfoButton = ({ gray, content, source })=>{
    return <InteractiveTooltip content={[
        {
            title: <FormattedMessage id="Information" defaultMessage="Information"/>,
            body: content,
            source: source
        }
    ]}>
      <button {...{
        0: {
            className: "xng3xce x1ghz6dp x1717udv x1rea2x4 x1e8rmrl x1tea8nt x1lugfcp x3nfvp2 xl56j7k x6s0dn4 x2lah0s x1td3qas x10w6t97 x11kkplg xegh2f2 x1ypdohk x1t137rt x784prv x9v5kkp x1ruszx x7s97pk x12fzoxp x1vob47u x268jtm"
        },
        1: {
            className: "xng3xce x1ghz6dp x1717udv x1rea2x4 x1e8rmrl x1tea8nt x1lugfcp x3nfvp2 xl56j7k x6s0dn4 x2lah0s x1td3qas x10w6t97 xegh2f2 x1ypdohk x1t137rt x784prv x9v5kkp x1ruszx x7s97pk x12fzoxp x1vob47u x268jtm x1n3tk9q"
        }
    }[!!gray << 0]}>
        <Info32/>
      </button>
    </InteractiveTooltip>;
};
function currentUrlWithFigureId(url, id) {
    const [withoutQuery] = url.split("?");
    const [withoutId] = withoutQuery.split("#");
    return `${withoutId}${id ? `#${id}` : ""}`;
}
export const ShareButton = ({ id, message, gray, disableEmbed, dataset })=>{
    const intl = useIntl();
    const screenshotUrl = typeof window !== "undefined" ? `${currentUrlWithFigureId(window.location.href, id)}` : process.env.SITE_URL;
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${encodeURIComponent(screenshotUrl)}&via=${"GEMReport"}`;
    const facebookUrl = `https://www.facebook.com/dialog/feed?app_id=${process.env.FACEBOOK_APP_ID}&link=${encodeURIComponent(screenshotUrl)}&caption=${encodeURIComponent(message)}&name=${encodeURIComponent(encodeURIComponent(intl.formatMessage(defineMessage({
        id: "meta.title",
        defaultMessage: "GEM Report Education Progress"
    }))))}`;
    const linkedinUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(screenshotUrl)}&title=${encodeURIComponent(intl.formatMessage(defineMessage({
        id: "meta.title",
        defaultMessage: "GEM Report Education Progress"
    })))}&summary=${encodeURIComponent(message)}`;
    const content = React.useMemo(()=>{
        const content = [
            {
                title: <FormattedMessage id="Share" defaultMessage="Share"/>,
                body: <ul>
            <ListLinkButton key="share.twitter" url={twitterUrl} icon={<SocialMediaTwitter16/>}>
              <FormattedMessage id="Twitter" defaultMessage="Twitter"/>
            </ListLinkButton>
            <ListLinkButton key="share.facebook" url={facebookUrl} icon={<SocialMediaFacebook16/>}>
              <FormattedMessage id="Facebook" defaultMessage="Facebook"/>
            </ListLinkButton>
            <ListLinkButton key="share.linkedin" url={linkedinUrl} icon={<SocialMediaLinkedin16/>}>
              <FormattedMessage id="LinkedIn" defaultMessage="LinkedIn"/>
            </ListLinkButton>
          </ul>
            }
        ];
        if (!disableEmbed && id) {
            content.push({
                title: <FormattedMessage id="Embed code" defaultMessage="Embed code"/>,
                body: <ul>
            <ListEmbedButton id={id} dataset={dataset}/>
          </ul>
            });
        }
        return content;
    }, [
        disableEmbed,
        facebookUrl,
        id,
        linkedinUrl,
        twitterUrl,
        dataset
    ]);
    return <InteractiveTooltip content={content}>
      <button {...{
        0: {
            className: "xng3xce x1ghz6dp x1717udv x1rea2x4 x1e8rmrl x1tea8nt x1lugfcp x3nfvp2 xl56j7k x6s0dn4 x2lah0s x1td3qas x10w6t97 x11kkplg xegh2f2 x1ypdohk x1t137rt x784prv x9v5kkp x1ruszx x7s97pk x12fzoxp x1vob47u x268jtm"
        },
        1: {
            className: "xng3xce x1ghz6dp x1717udv x1rea2x4 x1e8rmrl x1tea8nt x1lugfcp x3nfvp2 xl56j7k x6s0dn4 x2lah0s x1td3qas x10w6t97 xegh2f2 x1ypdohk x1t137rt x784prv x9v5kkp x1ruszx x7s97pk x12fzoxp x1vob47u x268jtm x1n3tk9q"
        }
    }[!!gray << 0]}>
        <Share32/>
      </button>
    </InteractiveTooltip>;
};
export const DownloadButton = ({ id, csv, xlsx, gray })=>{
    const downloadUrl = useStatefulFigureScreenshotUrl(id);
    return <InteractiveTooltip content={[
        {
            title: <FormattedMessage id="Download" defaultMessage="Download"/>,
            body: <ul>
              {pipe(downloadUrl, O.map((url)=><ListLinkButton key="download.image" url={url} icon={<DownloadImage16/>} timeout={DOWNLOAD_TIMEOUT} message={<FormattedMessage id="download.wait" defaultMessage="Download will start shortly …"/>}>
                    <FormattedMessage id="download.image" defaultMessage="Figure as .png image"/>
                  </ListLinkButton>), O.toNullable)}
              <ListLinkButton key="download.xlsx" url={xlsx} icon={<DownloadData16/>}>
                <FormattedMessage id="download.xlsx" defaultMessage="Data as Excel file"/>
              </ListLinkButton>
              <ListLinkButton key="download.csv" url={csv} icon={<DownloadData16/>}>
                <FormattedMessage id="download.csv" defaultMessage="Data as .csv file"/>
              </ListLinkButton>
            </ul>
        }
    ]}>
      <button {...{
        0: {
            className: "xng3xce x1ghz6dp x1717udv x1rea2x4 x1e8rmrl x1tea8nt x1lugfcp x3nfvp2 xl56j7k x6s0dn4 x2lah0s x1td3qas x10w6t97 x11kkplg xegh2f2 x1ypdohk x1t137rt x784prv x9v5kkp x1ruszx x7s97pk x12fzoxp x1vob47u x268jtm"
        },
        1: {
            className: "xng3xce x1ghz6dp x1717udv x1rea2x4 x1e8rmrl x1tea8nt x1lugfcp x3nfvp2 xl56j7k x6s0dn4 x2lah0s x1td3qas x10w6t97 xegh2f2 x1ypdohk x1t137rt x784prv x9v5kkp x1ruszx x7s97pk x12fzoxp x1vob47u x268jtm x1n3tk9q"
        }
    }[!!gray << 0]}>
        <Download32/>
      </button>
    </InteractiveTooltip>;
};
const listLinkButtonStyles = {
    root: {
        k1xSpc: "x78zum5",
        kGNEyG: "x6s0dn4",
        kogj98: "xyi5qop",
        kMwMTN: "xjvpvmj",
        kGzVvX: "x268jtm",
        kDPRdz: "x9a8edp",
        kdFj9n: "x1tgcp3",
        k6WrQY: "xusqnwr",
        kPJHz1: "x784prv",
        k7shQP: "x9v5kkp",
        kZs9gx: "x1ruszx",
        kiEn40: "x7s97pk",
        $$css: true
    }
};
const ListLinkButton = ({ children, icon, url, timeout, message })=>{
    const [isDownloading, setIsDownloading] = React.useState(false);
    const handleClick = React.useCallback(()=>{
        timeout != null && setIsDownloading(true);
    }, [
        timeout
    ]);
    React.useEffect(()=>{
        let timeoutId;
        if (timeout != null) {
            timeoutId = setTimeout(()=>setIsDownloading(false), timeout);
        }
        return ()=>clearTimeout(timeoutId);
    }, [
        timeout,
        isDownloading
    ]);
    return <li>
      <a href={url} {...stylex.props(typography.table, listLinkButtonStyles.root)} onClick={handleClick}>
        <div className="xng3xce x1ghz6dp x1717udv x1rea2x4 x1e8rmrl x1tea8nt x1lugfcp x3nfvp2 xl56j7k x6s0dn4 x2lah0s x1td3qas x10w6t97 x11kkplg xegh2f2 x1ypdohk x1t137rt x784prv x9v5kkp x1ruszx x7s97pk">{icon}</div>
        {isDownloading && message ? message : children}
      </a>
    </li>;
};
// `& input`/`& button` moved to direct element targeting below — this <li> always renders
// exactly one hardcoded <input> + <button> pair, not arbitrary children.
const listEmbedButtonStyles = {
    input: {
        kzqmXN: "x1oysuqx",
        kLKAdn: "x1yrsyyn",
        kpe85a: "xmzs88n",
        kGO01o: "x10b6aqq",
        kE3dHu: "xf18ygs",
        kWkggS: "x2pqsqo",
        kMzoRj: "xmkeg23",
        ksu8eU: "x1y0btm7",
        kVAM5u: "x1hhxfu6",
        kIxVMA: "x2b5lcy",
        kqGeR4: "x1orjc0r",
        k3XXqK: "x1t137rt",
        kMwMTN: "x1hcezkj",
        $$css: true
    }
};
const ListEmbedButton = ({ id, dataset })=>{
    const [success, setSuccess] = useStateWithAutoRevert({
        timeout: 1500,
        revertState: false
    });
    const url = useStatefulFigureEmbedUrl(id, dataset);
    return <li className="x78zum5 xzt5al7 x1e56ztr">
      {pipe(url, O.map((url)=>{
        const iframeHTML = `<iframe src="${url}" />`;
        return <>
              <input type="text" value={iframeHTML} disabled {...stylex.props(typography.table, listEmbedButtonStyles.input)}/>
              <button onClick={()=>{
            navigator.clipboard.writeText(iframeHTML);
            setSuccess(true);
        }} {...{
            0: {
                className: "xng3xce x1ghz6dp x1rea2x4 x1e8rmrl x1tea8nt x1lugfcp xe8ttls x1kfqdit x1lzl9kk x1nhu2vm x1vc0dw x1ypdohk xx025pi xxujjsa x784prv x9v5kkp x560pwp x7s97pk"
            },
            1: {
                className: "xng3xce x1ghz6dp x1rea2x4 x1e8rmrl x1tea8nt x1lugfcp xe8ttls x1lzl9kk x1nhu2vm x1vc0dw x1ypdohk xx025pi xxujjsa x784prv x9v5kkp x560pwp x7s97pk xqulxdc"
            }
        }[!!success << 0]}>
                <AnimatePresence mode="wait">
                  {success ? <motion.div key="success" {...M.opacityTransitionMotionProps}>
                      <Check16/>
                    </motion.div> : <motion.div key="copy" {...M.opacityTransitionMotionProps}>
                      <Copy16/>
                    </motion.div>}
                </AnimatePresence>
              </button>
            </>;
    }), O.toNullable)}
    </li>;
};
